import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { getData } from "../../axios/axiosHelper";

const FilterSelect = ({ label, value = "", onChange, children, className = "" }) => (
  <label className={`account-filter-field ${className}`.trim()}>
    <span>{label}</span>
    <select value={value} onChange={(event) => onChange(event.target.value)}>
      {children}
    </select>
  </label>
);

const AccountFilters = ({
  type,
  filters,
  searchDraft = "",
  onSearchChange = () => {},
  onChange,
  onClear,
  text,
  language
}) => {
  const [options, setOptions] = useState([]);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customRange, setCustomRange] = useState({ from: "", to: "" });

  useEffect(() => {
    const endpoint = type === "customers" ? "/country?perPage=100" : "/category?perPage=100";
    getData(endpoint, true)
      .then((response) => setOptions(response?.data?.data || []))
      .catch(() => setOptions([]));
  }, [language, type]);

  useEffect(() => {
    setCustomRange({
      from: filters.registeredFrom || "",
      to: filters.registeredTo || ""
    });
  }, [filters.registeredFrom, filters.registeredTo]);

  const optionName = (item) =>
    item[`name${language === "ar" ? "Ar" : "En"}`] || item.name || item.nameEn || item.nameAr;
  const inferredCountry = filters.governorate && !filters.country
    ? options.find((country) => (country.governorates || []).some(
        (governorate) => governorate._id === filters.governorate
      ))?._id
    : "";
  const selectedCountry = filters.country || inferredCountry;
  const selectedLocation = filters.governorate
    ? selectedCountry
      ? `city:${selectedCountry}:${filters.governorate}`
      : ""
    : selectedCountry
      ? `country:${selectedCountry}`
      : "";

  const changeLocation = (value) => {
    if (!value) {
      onChange({ country: "", governorate: "" });
      return;
    }
    const [level, country, governorate = ""] = value.split(":");
    onChange({
      country,
      governorate: level === "city" ? governorate : ""
    });
  };

  const validCustomRange = Boolean(
    customRange.from && customRange.to && customRange.from <= customRange.to
  );
  const selectedCustomRange = [filters.registeredFrom, filters.registeredTo]
    .filter(Boolean)
    .join(" — ");
  const hasActiveFilters = Boolean(
    searchDraft.trim() ||
    (filters.lifecycleState && filters.lifecycleState !== "ACTIVE") ||
    filters.country ||
    filters.governorate ||
    filters.gender ||
    filters.category ||
    (filters.datePreset && filters.datePreset !== "all") ||
    filters.sort === "registeredAt"
  );

  const changeDatePreset = (value) => {
    if (value === "custom") {
      setShowCustomRange(true);
      return;
    }
    onChange({
      datePreset: value,
      registeredFrom: "",
      registeredTo: ""
    });
  };

  const applyCustomRange = () => {
    if (!validCustomRange) return;
    onChange({
      datePreset: "custom",
      registeredFrom: customRange.from,
      registeredTo: customRange.to
    });
    setShowCustomRange(false);
  };

  return (
    <>
      <div
        className={`account-toolbar account-directory-filter-bar is-${type}`}
        aria-label={text.filters}
      >
        <label className="account-search">
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
          <input
            type="search"
            value={searchDraft}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={type === "customers" ? text.searchCustomers : text.searchMerchants}
          />
        </label>
        <FilterSelect
          label={text.status}
          value={filters.lifecycleState || "ACTIVE"}
          onChange={(value) => onChange({ lifecycleState: value })}
        >
          <option value="ALL">{text.all}</option>
          <option value="ACTIVE">{text.active}</option>
          <option value="BLOCKED">{text.blocked}</option>
          <option value="DELETED">{text.deleted}</option>
        </FilterSelect>
        {type === "customers" ? (
          <label className="account-filter-field account-location-filter">
            <span>{text.locations}</span>
            <select
              aria-label={text.locations}
              value={selectedLocation}
              onChange={(event) => changeLocation(event.target.value)}
            >
              <option value="">{text.allLocations}</option>
              {options.map((country) => {
                const countryName = optionName(country);
                return (
                  <optgroup key={country._id} label={countryName}>
                    <option value={`country:${country._id}`}>
                      {countryName} — {text.allCities}
                    </option>
                    {(country.governorates || []).map((governorate) => (
                      <option
                        key={governorate._id}
                        value={`city:${country._id}:${governorate._id}`}
                      >
                        {optionName(governorate)}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </label>
        ) : (
          <FilterSelect
            label={text.category}
            value={filters.category || ""}
            onChange={(value) => onChange({ category: value })}
            className="account-category-filter"
          >
            <option value="">{text.all}</option>
            {options.map((item) => <option key={item._id} value={item._id}>{optionName(item)}</option>)}
          </FilterSelect>
        )}
        {type === "customers" && (
          <FilterSelect
            label={text.gender}
            value={filters.gender || ""}
            onChange={(value) => onChange({ gender: value })}
          >
            <option value="">{text.all}</option>
            <option value="Male">{text.male}</option>
            <option value="Female">{text.female}</option>
          </FilterSelect>
        )}
        <div className="account-filter-field">
          <span>{text.registrationDate}</span>
          <div className="registration-review-date-filter-control">
            <select
              aria-label={text.registrationDate}
              className={filters.datePreset === "custom" ? "has-custom-range" : ""}
              value={filters.datePreset || "all"}
              onChange={(event) => changeDatePreset(event.target.value)}
            >
              <option value="all">{text.allDates}</option>
              <option value="today">{text.today}</option>
              <option value="last7">{text.last7Days}</option>
              <option value="last30">{text.last30Days}</option>
              <option value="last90">{text.last90Days}</option>
              <option value="custom">
                {filters.datePreset === "custom" && selectedCustomRange
                  ? selectedCustomRange
                  : text.customRange}
              </option>
            </select>
            {filters.datePreset === "custom" && (
              <button
                type="button"
                className="account-custom-range-edit"
                onClick={() => setShowCustomRange(true)}
                aria-label={text.editCustomRange}
              >
                <i className="fa-solid fa-chevron-down" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
        <FilterSelect
          label={text.sortBy}
          value={filters.sort || "-registeredAt"}
          onChange={(value) => onChange({ sort: value })}
        >
          <option value="-registeredAt">{text.newest}</option>
          <option value="registeredAt">{text.oldest}</option>
        </FilterSelect>
        <button
          type="button"
          className={`account-clear-filters ${hasActiveFilters ? "" : "is-placeholder"}`}
          onClick={onClear}
          disabled={!hasActiveFilters}
          tabIndex={hasActiveFilters ? 0 : -1}
          aria-hidden={!hasActiveFilters}
        >
          {text.clearFilters}
        </button>
      </div>

      <Modal
        show={showCustomRange}
        onHide={() => setShowCustomRange(false)}
        centered
        aria-labelledby="account-registration-date-range-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="account-registration-date-range-title">
            {text.customRegistrationRange}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="registration-review-date-range">
            <label className="account-filter-field">
              <span>{text.fromDate}</span>
              <input
                aria-label={text.fromDate}
                type="date"
                value={customRange.from}
                max={customRange.to || undefined}
                onChange={(event) => setCustomRange((current) => ({
                  ...current,
                  from: event.target.value
                }))}
              />
            </label>
            <label className="account-filter-field">
              <span>{text.toDate}</span>
              <input
                aria-label={text.toDate}
                type="date"
                value={customRange.to}
                min={customRange.from || undefined}
                onChange={(event) => setCustomRange((current) => ({
                  ...current,
                  to: event.target.value
                }))}
              />
            </label>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCustomRange(false)}>
            {text.cancel}
          </Button>
          <Button variant="primary" disabled={!validCustomRange} onClick={applyCustomRange}>
            {text.apply}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AccountFilters;
